import { useState, useEffect, useCallback } from "react";
import {
  deleteFixedQuest,
  deletePenaltyQuest,
  getDailyQuestRow,
  upsertDailyQuestRow,
  upsertFixedQuest,
} from "../api/supabaseData";
import { getCurrentQuestDate } from "../utils/dateUtils";

export function useDailyQuests(uid, dateStr) {
  const effectiveDateStr = dateStr ?? getCurrentQuestDate();
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);

  const replaceQuests = useCallback((nextQuests) => {
    setQuests(Array.isArray(nextQuests) ? nextQuests : []);
  }, []);

  // The active quest board is reconciled during backend-owned day initialization.
  useEffect(() => {
    if (!uid || !effectiveDateStr) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const dailyRow = await getDailyQuestRow(uid, effectiveDateStr);
        if (!cancelled) {
          setQuests(dailyRow?.quests || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load daily quests:", error);
          setQuests([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid, effectiveDateStr]);

  // -- 2. Add Quest (add to fixed if fixed) --
  const addQuest = useCallback(async (quest) => {
    const dailyRow = await getDailyQuestRow(uid, effectiveDateStr);
    let questsArr = dailyRow?.quests || [];
    const questWithId = { ...quest, id: quest.id || Date.now().toString() };
    questsArr.push(questWithId);
    await upsertDailyQuestRow(uid, effectiveDateStr, questsArr, dailyRow?.createdAt || null);
    setQuests(questsArr);
    // If fixed, add to /fixedQuests too
    if (quest.fixed) {
      await upsertFixedQuest(uid, questWithId.id, questWithId);
    }
  }, [uid, effectiveDateStr]);

  // -- 3. Update Quest --
  const updateQuest = useCallback(async (questId, changes) => {
    const dailyRow = await getDailyQuestRow(uid, effectiveDateStr);
    if (!dailyRow) return;
    const previousQuest = (dailyRow.quests || []).find((q) => q.id === questId);
    const questsArr = (dailyRow.quests || []).map(q =>
      q.id === questId ? { ...q, ...changes } : q
    );
    await upsertDailyQuestRow(uid, effectiveDateStr, questsArr, dailyRow.createdAt || null);
    setQuests(questsArr);
    // Update in fixedQuests if fixed
    const q = questsArr.find(q => q.id === questId);
    if (q?.fixed) {
      await upsertFixedQuest(uid, questId, q);
    } else if (previousQuest?.fixed) {
      await deleteFixedQuest(uid, questId);
    }
  }, [uid, effectiveDateStr]);

  // -- 4. Delete Quest --
  const deleteQuest = useCallback(async (questId) => {
    const dailyRow = await getDailyQuestRow(uid, effectiveDateStr);
    if (!dailyRow) return;
    const questsArr = (dailyRow.quests || []).filter(q => q.id !== questId);
    await upsertDailyQuestRow(uid, effectiveDateStr, questsArr, dailyRow.createdAt || null);
    setQuests(questsArr);
    // Also delete from fixedQuests if it's fixed
    await deleteFixedQuest(uid, questId);
    // Also delete from penaltyQuests for today if present
    await deletePenaltyQuest(uid, effectiveDateStr, questId);
  }, [uid, effectiveDateStr]);

  return {
    quests,
    loading,
    addQuest,
    updateQuest,
    deleteQuest,
    replaceQuests,
  };
}
